import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/services/api.client'

/**
 * Sticky banner shown on every authenticated page when the active session is
 * an impersonation. Reads `impersonatedBy` from the `/auth/me` viewer (the
 * backend resolves the JWT claim and passes it through). "Return to self"
 * POSTs `/auth/dev/stop-impersonating` — the backend swaps `access_token`
 * back to the original admin JWT server-side, and we reload.
 */
export function ImpersonationBanner() {
	const { user } = useAuth()
	const [isStopping, setIsStopping] = useState(false)

	if (!user?.impersonatedBy) return null

	const handleReturn = () => {
		setIsStopping(true)
		// Best-effort: swallow the rejection. Even on failure, reload so the
		// user sees fresh state rather than a stuck banner. The server-side
		// cookie state is the source of truth; reload re-reads it via /auth/me.
		void apiClient
			.post('/auth/dev/stop-impersonating')
			.catch(() => undefined)
			.finally(() => window.location.reload())
	}

	return (
		<div
			role="alert"
			className="sticky top-0 z-50 bg-amber-100 border-b border-amber-300 text-amber-900 text-sm flex items-center gap-3 px-4 py-2"
		>
			<span className="font-semibold">Impersonating:</span>
			<span>{user.email}</span>
			<span className="flex-1" />
			<button
				type="button"
				onClick={handleReturn}
				disabled={isStopping}
				className="rounded-md bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
			>
				Return to self
			</button>
		</div>
	)
}
